import React , { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import CameraPreview from './CameraPreview';
import useCodingStore from './CodingStore';
import Hint from './Hint';
import { AssessmentStates, InterviewerStates, QuestionStates } from './constant';
import { CustomTooltip } from 'components/InfoTooltip';
import { TonalButton } from 'components/CustomButton';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '250px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(5),
  },
  previews: {
    width: '100%',
    height: '250px',
    overflow: 'hidden',
    borderRadius: '4px',
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200']
  },
  askInterviewer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    position : 'relative'
  },
  askButton: {
    width: '100%',
    backgroundColor: theme.palette.primary['clr-50'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    borderRadius: '4px',
    cursor : 'pointer'
  },
  disableHint: {
    backgroundColor: theme.palette.neutral['clr-300'],
  },
  interviewerContainer: {
    position: 'relative',
    width: '100%', height: '100%'
  }
}));

const videoSX = {
  objectFit: 'cover',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  transition: 'opacity 0.8s ease-in-out',
}

const VideoPreview = ({ play=false }) => {
  const classes = useStyles();
  const waitingRef = React.useRef(null);

  const assessmentState = useCodingStore(state => state.assessmentState);

  React.useEffect(() => {
    if (waitingRef.current) {
      if (assessmentState === AssessmentStates.RUNNING) {
        const timeoutId = setTimeout(() => {
          waitingRef.current.play().catch(err => console.error('Play error:', err));
        }, 1500); 
        return () => clearTimeout(timeoutId);
      } else {
        waitingRef.current.pause();
      }
    }
  }, [assessmentState]);

  return (
    <Box id="interviewer-container" className={classes.interviewerContainer}>
      <video
        ref={waitingRef}
        src="https://assets.languify.in/video/interviewer_waiting.mp4"
        muted
        loop
        playsInline
        style={{
          ...videoSX,
          opacity: play ? 0 : 1,
          zIndex: play ? 1 : 2,
        }}
      />
      
      <video
        src="https://assets.languify.in/video/interviewer_speaking.mp4"
        muted
        autoPlay
        loop
        playsInline
        style={{
          ...videoSX,
          opacity: play ? 1 : 0,
          zIndex: play ? 2 : 1,
        }}
      />
    </Box>
  );
};

const InterviewerSection = () => {
  const classes = useStyles();
  
  const [openHints , setOpenHints] = useState(false);

  const interviewerState = useCodingStore(state => state.interviewerState);
  const questionState = useCodingStore(state => state.questionState);
  const triggerHint = useCodingStore(state => state.triggerHint);

  const {tooltipHint, enable=false} = React.useMemo(() => {
    let tooltipHint = '';
    let enable = false;
    if (questionState === QuestionStates.INTERVIEWER_ASKING) {
      tooltipHint= 'Hold on! Let your interviewer complete first, listen closely before asking your query!';
    } else if (questionState === QuestionStates.CODING) {
      tooltipHint= <>If you like, you can ask the <br/>interviewer for a hint.</>;
      enable = true;
    } else if(questionState === QuestionStates.FETCHING_QUESTIONS) {
      tooltipHint= 'Wait for the followup question before taking a hint for it.';
    } else {
      tooltipHint= 'You can take Hint only while coding.'
    }

    return { tooltipHint, enable };
  }, [questionState])

  return (
    <Box className={classes.root}>
      <Box id='interviewer' className={classes.previews}>
        <VideoPreview play={interviewerState === InterviewerStates.SPEAKING}/>
      </Box>
      <Box className={classes.previews}>
        <CameraPreview/>
      </Box>
      <Box   className={classes.askInterviewer}>
        <Box 
          id='ask-interviewer' 
          sx={{
            position : 'absolute' , top : '65px' , right : 0 , width : '20px' , height : '20px'
            }}>
            <Box id='ask-interviewer-container'></Box>
        </Box>
        <Typography  variant="h6-medium">
          Having trouble? Get the personalized support you need
        </Typography>
        <CustomTooltip
          placement='top'
          show={triggerHint}
          component={
            <Typography variant="body02-semiBold" color="shades.clr-offWhite-900">
              {tooltipHint}
            </Typography>
          }
          tooltipStyle={{
            backgroundColor: enable ? '#1961BF' : '#000', 
            textAlign: 'center' , cursor : 'default'
          }}
          arrowStyle={{color: enable ? '#1961BF' : '#000'}}
        >
          <Box>
            <TonalButton
              variant='outlined' color='primary' style={{ width: '100%' }}
              disabled={!enable}
              onClick={() => questionState === QuestionStates.CODING ? setOpenHints(true) : null}
            >
              <Typography variant="body01-bold" color={!enable ? 'neutral.clr-600' : "primary.main"}>
                Ask your interviewer
              </Typography>
            </TonalButton>
          </Box>
        </CustomTooltip>
      </Box>
      {
        openHints &&
          <Hint open={openHints} onClose={()=>setOpenHints(false)}/>
      }
    </Box>
  );
}

export default InterviewerSection;